import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Download, RefreshCw, Trash2 } from "lucide-react";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS_CHIP } from "./RunsPanel";

const fmtQar = (v) => `QAR ${Number(v || 0).toLocaleString()}`;
const NEXT = { draft: "reviewed", reviewed: "approved", approved: "paid" };
const NEXT_LABEL = { reviewed: "Mark Reviewed", approved: "Approve Run", paid: "Mark Paid" };
const NEXT_ROLES = { reviewed: ["admin", "hr_manager"], approved: ["admin"], paid: ["admin"] };

export const RunDetail = ({ runId, onBack }) => {
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [onlyExceptions, setOnlyExceptions] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/payroll/runs/${runId}`).then((r) => setRun(r.data));
  useEffect(() => { load(); }, [runId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!run) return <Skeleton className="h-96 w-full rounded-none" />;

  const next = NEXT[run.status];
  const canTransition = next && NEXT_ROLES[next].includes(user.role);
  const lines = onlyExceptions ? run.lines.filter((l) => l.exceptions.length) : run.lines;

  const doTransition = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${runId}/transition`, { to: next });
      toast.success(`Run ${run.period} → ${next.toUpperCase()}`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const recompute = async () => {
    setBusy(true);
    try {
      await api.post(`/payroll/runs/${runId}/recompute`);
      toast.success("Run recomputed with current settings and KPI inputs");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    try {
      await api.delete(`/payroll/runs/${runId}`);
      toast.success("Draft run deleted");
      onBack();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="run-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onBack} data-testid="run-back-btn"
            className="rounded-none h-8 text-[12px] text-muted-foreground -ml-2">
            <ArrowLeft className="h-4 w-4" /> Runs
          </Button>
          <h2 className="text-lg font-bold text-[var(--brand-primary)] font-mono">{run.period}</h2>
          <span className={`text-[10px] font-mono px-2 py-1 uppercase ${STATUS_CHIP[run.status]}`} data-testid="run-status-chip">
            {run.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {run.status === "draft" && (
            <>
              <Button variant="outline" onClick={recompute} disabled={busy} data-testid="run-recompute-btn"
                className="rounded-none h-8 text-[12px]">
                <RefreshCw className="h-3.5 w-3.5" /> Recompute
              </Button>
              {user.role === "admin" && (
                <Button variant="outline" onClick={remove} data-testid="run-delete-btn"
                  className="rounded-none h-8 text-[12px] text-red-700 border-red-300 hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </Button>
              )}
            </>
          )}
          {next && (
            <Button onClick={doTransition} disabled={!canTransition || busy} data-testid="run-transition-btn"
              title={canTransition ? "" : `Requires role: ${NEXT_ROLES[next].join(", ")}`}
              className="rounded-none h-8 text-[12px] bg-teal-600 hover:bg-teal-700 disabled:opacity-50">
              {NEXT_LABEL[next]}
            </Button>
          )}
          {next && !canTransition && (
            <span className="text-[11px] text-muted-foreground">admin only</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Headcount", run.totals.headcount], ["Gross", fmtQar(run.totals.gross)],
          ["Incentives", fmtQar(run.totals.incentives)], ["Deductions", fmtQar(run.totals.deductions)],
          ["Net", fmtQar(run.totals.net)]].map(([l, v]) => (
          <div key={l} className="bg-white border border-border px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{l}</p>
            <p className="text-[16px] font-bold text-[var(--brand-primary)] mt-1 font-mono">{v}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button data-testid="filter-all-btn" onClick={() => setOnlyExceptions(false)}
          className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border ${!onlyExceptions ? "bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]" : "bg-white text-slate-600 border-border"}`}>
          All lines ({run.lines.length})
        </button>
        <button data-testid="filter-exceptions-btn" onClick={() => setOnlyExceptions(true)}
          className={`text-[11px] uppercase tracking-wider px-3 py-1.5 border ${onlyExceptions ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-border"}`}>
          Exceptions ({run.exceptions_count})
        </button>
      </div>

      <div className="bg-white border border-border overflow-x-auto">
        <table className="w-full text-[12px] min-w-[1080px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border bg-slate-50">
              {["Employee", "Dept", "Nat.", "Basic", "Allowances", "KPI", "Tier", "Certs", "Mult.", "Incentive", "Pension", "Net", "Δ Prev", "Flags", ""].map((h, i) => (
                <th key={i} className="px-3 py-2 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {lines.map((l) => {
              const delta = l.prev_net ? Math.round(((l.net - l.prev_net) / l.prev_net) * 100) : null;
              return (
                <tr key={l.employee_id} data-testid={`line-${l.employee_id}`}
                  className={l.exceptions.length ? "bg-amber-50/50" : ""}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <p className="font-medium text-slate-800">{l.name}</p>
                    <p className="text-[10px] font-mono text-slate-400">{l.employee_id}</p>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{l.department}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">{l.nationality}</td>
                  <td className="px-3 py-2 font-mono">{Number(l.basic).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono">{Number(l.housing + l.transport + l.other_allowance).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    {l.kpi_pct != null ? (
                      <span title={l.kpi_source}>
                        {l.kpi_pct}%
                        <span className="text-[9px] font-mono text-slate-400 block">{l.kpi_source?.startsWith("review") ? "review" : "kpi"}</span>
                      </span>
                    ) : <span className="text-[10px] font-mono text-red-600">NO KPI</span>}
                  </td>
                  <td className="px-3 py-2 font-mono">{l.applied_pct}%</td>
                  <td className="px-3 py-2">{l.cert_count || "—"}</td>
                  <td className="px-3 py-2 font-mono">{l.dept_multiplier > 1 ? `×${l.dept_multiplier}` : "—"}</td>
                  <td className="px-3 py-2 font-mono text-teal-700">{Number(l.incentive).toLocaleString()}</td>
                  <td className="px-3 py-2 font-mono">{l.pension ? Number(l.pension).toLocaleString() : "—"}</td>
                  <td className="px-3 py-2 font-mono font-semibold">{Number(l.net).toLocaleString()}</td>
                  <td className="px-3 py-2">
                    {delta != null
                      ? <span className={`font-mono text-[11px] ${Math.abs(delta) > 20 ? "text-red-600 font-bold" : "text-slate-500"}`}>{delta > 0 ? "+" : ""}{delta}%</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    {l.exceptions.map((x) => (
                      <span key={x} className="text-[9px] font-mono bg-amber-100 border border-amber-300 text-amber-900 px-1 py-0.5 mr-1 whitespace-nowrap">
                        {x === "missing_kpi" ? "MISSING KPI" : "NET Δ>20%"}
                      </span>
                    ))}
                    {l.adjustment_note && (
                      <span className="text-[9px] font-mono bg-slate-100 border border-slate-300 text-slate-700 px-1 py-0.5 whitespace-nowrap" title={l.adjustment_note}>ADJ</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button data-testid={`payslip-btn-${l.employee_id}`} title="Download payslip PDF"
                      onClick={() => downloadFile(`/payroll/runs/${runId}/payslip/${l.employee_id}`, `payslip-${run.period}-${l.employee_id}.pdf`)}
                      className="text-slate-400 hover:text-teal-700">
                      <Download className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-border px-4 py-3" data-testid="run-transitions">
        <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-2">Audit trail</p>
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          {run.transitions.map((t, i) => (
            <span key={i} className="text-[11px] font-mono text-slate-600">
              {t.from || "∅"} → <span className="text-teal-700 font-semibold">{t.to}</span> · {t.by} · {String(t.at).slice(0, 16).replace("T", " ")}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RunDetail;
